// SPEC-16 · Avaliações, a tab under the navigation pill since SPEC-27.
// It shows the state the device holds at once, then the server's when it
// arrives, and stays usable offline with the last state it knows. Opening
// an assessment from here records the hub as its origin, where « Sair »
// and its end lead back.
//
// Where an assessment sits, how many are done and how many minutes are
// left are poweryourflow-lib's (hubSummary, ASSESSMENT_FACTS); a row's
// "n / total" is the run's own count. This screen arranges and words them,
// in the app's language (SPEC-31): titles, groups, tags, metas and dates.

import { useEffect, useState } from "react";
import { ASSESSMENT_FACTS, ageOn, hubSummary } from "poweryourflow-lib";
import type { HubGroup, HubPlacement } from "poweryourflow-lib";

import { Avatar } from "../components/Avatar";
import { NavPill } from "../components/NavPill";
import { todayLine } from "../i18n/language";
import type { Copy } from "../i18n/language";
import { useCopy, useLanguageFade } from "../i18n/LanguageProvider";
import { rememberOrigin } from "../lib/place";
import { assessmentPaths, paths, useRouter } from "../lib/router";
import { localToday } from "../onboarding/conversation";
import type { AssessmentId, AssessmentRun, Profile } from "../schema/types";
import { useHub } from "./useHub";
import type { HubSource } from "./useHub";

/** The groups in the card's order; a group with no row is not shown. */
const GROUPS: readonly HubGroup[] = ["progress", "todo", "done"];

/** Whether the device has a network, following the browser's online and offline events. */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}

const card = "rounded-card bg-white p-5";
const label = "text-label font-semibold tracking-[var(--ls-label)] uppercase";

function ProfileCard({ profile, complete, onOpen }: { profile: Profile | null; complete: boolean; onOpen: () => void }) {
  const text = useCopy().assessments;
  const firstName = profile?.firstName ?? "";
  const age = complete && profile?.birthDate ? ageOn(profile.birthDate, localToday()) : undefined;
  const line =
    age !== undefined ? text.profileLine(age, profile?.modality ?? "", profile?.position ?? "") : text.completeProfile;
  return (
    <button type="button" onClick={onOpen} className={`${card} flex w-full items-center gap-4 text-left`}>
      <Avatar firstName={firstName} size="medium" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-title font-semibold tracking-[var(--ls-title)]">{firstName}</span>
        <span className="text-body-sm text-muted">{line}</span>
      </span>
      <span aria-hidden="true" className="text-title text-dark">
        →
      </span>
    </button>
  );
}

function ProgressCard({ done, total, remaining, minutesLeft }: { done: number; total: number; remaining: number; minutesLeft: number }) {
  const text = useCopy().assessments;
  return (
    <div data-testid="progress" className={card}>
      <div className="flex items-end justify-between">
        <p className="text-body font-semibold text-lime-ink">{text.doneLabel}</p>
        <p className="text-numeral leading-[var(--lh-tight)] font-medium tracking-[var(--ls-heading)] text-dark">
          {done}
          <span className="text-[length:var(--numeral-unit)] text-lime-ink">/{total}</span>
        </p>
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-pill bg-line-10">
        {/* A transform, as in Dados gerais; it does not move, so reduced motion has nothing to hold. */}
        <div className="h-full w-full rounded-pill bg-lime" style={{ transform: `translateX(${(done / total - 1) * 100}%)` }} />
      </div>
      <p className="mt-3 text-body-sm text-muted">{remaining === 0 ? text.allDone : text.left(remaining, minutesLeft)}</p>
    </div>
  );
}

function Tag({ placement, run }: { placement: HubPlacement; run: AssessmentRun | null }) {
  const text = useCopy().assessments;
  const tag = "shrink-0 rounded-pill px-3 py-1.5 text-caption font-semibold";
  if (placement.group === "done") return <span className={`${tag} bg-lime-tint text-lime-ink`}>{text.tagDone}</span>;
  if (placement.group === "progress" && run) {
    return <span className={`${tag} bg-violet-tint text-violet-ink`}>{text.tagProgress(run.answeredCount, run.visibleTotal)}</span>;
  }
  return <span className={`${tag} border border-line-20 text-dark`}>{text.tagTodo}</span>;
}

function metaOf(text: Copy["assessments"], placement: HubPlacement): string {
  const { kicker } = text.rows[placement.assessmentId];
  if (placement.group === "progress") return text.progressMeta(kicker);
  if (placement.group === "done" && placement.doneAt !== undefined) {
    const doneAt = new Date(placement.doneAt);
    // Dados gerais is edited from here (« Editar »); every other finished assessment opens read-only (SPEC-28).
    return text.doneMeta(kicker, doneAt.getDate(), text.months[doneAt.getMonth()] ?? "", placement.assessmentId === "dados");
  }
  const facts = ASSESSMENT_FACTS[placement.assessmentId];
  return text.todoMeta(kicker, facts.count, text.units[facts.unit], facts.minutes);
}

function Row({ placement, run, onOpen }: { placement: HubPlacement; run: AssessmentRun | null; onOpen: () => void }) {
  const text = useCopy().assessments;
  const row = text.rows[placement.assessmentId];
  return (
    <li>
      <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 rounded-card bg-white px-4 py-4 text-left">
        <span aria-hidden="true" className="flex size-10 shrink-0 items-center justify-center rounded-glyph bg-cream-deep font-semibold text-muted">
          {row.letter}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-body font-semibold">{row.title}</span>
          <span className="text-caption text-muted">{metaOf(text, placement)}</span>
        </span>
        <Tag placement={placement} run={run} />
      </button>
    </li>
  );
}

/** Cream blocks where the cards and rows will be, no spinner (SPEC-16, Action list). */
function Skeleton() {
  const text = useCopy().assessments;
  const block = "rounded-card bg-cream-deep";
  return (
    <div role="status" aria-label={text.loading} className="flex flex-col gap-3">
      <div className={`${block} h-20`} />
      <div className={`${block} h-32`} />
      <div className={`${block} h-18`} />
      <div className={`${block} h-18`} />
      <div className={`${block} h-18`} />
    </div>
  );
}

export function Assessments({ source }: { source: HubSource }) {
  const { navigate } = useRouter();
  const words = useCopy();
  const text = words.assessments;
  const fade = useLanguageFade();
  const online = useOnline();
  const { hub, failed, retry, runOf } = useHub(source, "hub");

  const summary = hub ? hubSummary(runOf) : null;
  const firstName = hub?.profile?.firstName ?? "";
  const openProfile = () => navigate(paths.profile);
  const open = (assessmentId: AssessmentId) => {
    rememberOrigin(assessmentId, "avaliacoes");
    navigate(assessmentPaths[assessmentId]);
  };

  return (
    <main className="flex min-h-dvh flex-col bg-cream">
      <NavPill active="assessments" firstName={firstName} />

      {!online && (
        <p role="status" className="mx-5 mt-2 rounded-pill bg-cream-deep px-4 py-2 text-center text-caption text-muted">
          {text.offline}
        </p>
      )}

      <div className={`${fade || "tab-enter"} flex flex-col gap-3 px-5 pt-4 pb-8`}>
        <p className="text-caption text-muted">{todayLine(words, new Date())}</p>
        <h1 className="pb-2 text-display leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)]">{text.title}</h1>

        {failed && (
          <div className="flex flex-col gap-3">
            <p className="text-body leading-[var(--lh-body)] text-muted">{text.failed}</p>
            <button
              type="button"
              onClick={retry}
              className="flex h-[var(--control-h)] w-full items-center justify-center rounded-pill bg-dark text-body font-semibold text-cream hover:bg-dark-hover"
            >
              {text.retry}
            </button>
          </div>
        )}

        {!failed && !summary && <Skeleton />}

        {!failed && hub && summary && (
          <>
            <ProfileCard
              profile={hub.profile}
              complete={summary.placements[0]?.group === "done"}
              onOpen={openProfile}
            />
            <ProgressCard {...summary} />
            {GROUPS.map((group) => {
              const rows = summary.placements.filter((placement) => placement.group === group);
              if (rows.length === 0) return null;
              return (
                <section key={group} aria-label={text.groups[group]} className="flex flex-col gap-2 pt-3">
                  <h2 className={`${label} px-1 text-muted`}>{text.groups[group]}</h2>
                  <ul className="flex flex-col gap-2">
                    {rows.map((placement) => (
                      <Row
                        key={placement.assessmentId}
                        placement={placement}
                        run={runOf(placement.assessmentId)}
                        onOpen={() => open(placement.assessmentId)}
                      />
                    ))}
                  </ul>
                </section>
              );
            })}
          </>
        )}
      </div>
    </main>
  );
}
